import type { SxProps, Theme } from '@mui/material/styles';
import MaterialTableRow, { type TableRowProps as MaterialTableRowProps } from '@mui/material/TableRow';
import type { BaseRow, Columns, RowAction } from '@openx/types/tableHelpers';
import { STICKY_POSITION } from '@openx/types/tableHelpers';
import type { JSX, ReactNode } from 'react';

import type { HighlightRules } from '../highlightRules';

import { calculateStickyLeftOffset, calculateStickyRightPropsForRow } from './sections/utils';
import { GenericTableCell as TableCell } from './TableCell';

export interface TableRowProps<RowT extends BaseRow> extends MaterialTableRowProps {
  rowData?: RowT;
  onRowClick?: RowAction<RowT>;
  columns: Columns<RowT>;
  highlightRules?: HighlightRules;
  'data-test'?: string;
  selected?: boolean;
  iconCellRenderer?: (data: RowT) => ReactNode;
  className?: string;
  isClickable?: boolean;
  overrideTableCellStyles?: SxProps<Theme>;
  stickyActionColumn?: boolean;
}

export type TableRowPropsWithData<RowT extends BaseRow> = TableRowProps<RowT> & { rowData: RowT };

export function TableRow<RowT extends BaseRow>(props: Readonly<TableRowProps<RowT>>): JSX.Element {
  const {
    onRowClick,
    columns,
    rowData,
    highlightRules,
    selected,
    iconCellRenderer,
    className,
    isClickable,
    overrideTableCellStyles,
    stickyActionColumn,
  } = props;
  const onClick = onRowClick && rowData ? e => onRowClick(rowData, e) : undefined;

  const firstNonCheckboxIndex = columns.findIndex(column => column.key !== 'select');

  return (
    <MaterialTableRow
      onClick={onClick}
      hover
      className={className}
      data-test={props['data-test'] ?? 'table-row'}
      selected={selected}
      sx={{
        cursor: isClickable ? 'pointer' : 'default',
        ...props.sx,
      }}
    >
      {columns.map((column, index) => {
        const { stickyRightOffset, isFirstStickyRight } = calculateStickyRightPropsForRow(
          column,
          index,
          columns,
          stickyActionColumn
        );

        return (
          <TableCell
            column={column}
            rowData={rowData}
            highlightRules={highlightRules}
            key={column.key}
            overrideTableCellStyles={overrideTableCellStyles}
            stickyLeftOffset={
              column.sticky === STICKY_POSITION.LEFT ? calculateStickyLeftOffset(columns, index) : undefined
            }
            stickyRightOffset={stickyRightOffset}
            isFirstNonCheckbox={firstNonCheckboxIndex === 0 && index === 0}
            isLeadingStickyRight={isFirstStickyRight}
          />
        );
      })}
      {rowData && iconCellRenderer?.(rowData)}
    </MaterialTableRow>
  );
}
